import { EntityState } from '@reduxjs/toolkit';

import { IItem, ReferenceType } from '@/interfaces/grpc.interfaces';
import instanceOfReference from '@/utils/instanceOfReference';

import { ReferenceItemType } from '../types';
import { referencesSelectors } from '..';

/**
 * Выполняет сопоставление свойств типа ReferenceType в объектах items
 * значениями из состояния referencesState.
 * @param referencesState слайс, хранящий значения для объектов ReferenceType
 * @param items массив объектов, для свойств которых необходимо найти значения.
 * Поиск значений производится только для свойств типа ReferenceType.
 *
 * @returns Объект со свойствами:
 * partiallyExtendedItems: массив, содержащий переданные объекты, но с уже расширенными свойствами
 * nonExtendedReferences: массив ссылок на свойства ReferenceType из переданных items, которые
 * содержатся в объектах items, но отсутствуют в стейте @param referencesState. В последствии их нужно будет расширить значениями @see modifyReferences()
 * uniqueNonExtendedReferences: Map из уникальных ReferenceType, для которых необходимо получить значения с сервера.
 */
const getPartiallyExtendedItems = (
  referencesState: EntityState<ReferenceItemType, ReferenceItemType['id']>,
  items: Array<IItem>,
) => {
  const partiallyExtendedItems: Array<IItem> = [];
  const nonExtendedReferences: Array<ReferenceType> = [];
  const uniqueNonExtendedReferences: Map<string, ReferenceType> = new Map();

  for (const currentItem of items) {
    if (!currentItem.data) {
      continue;
    }

    // Проходим по всем значениям, расширяемого объекта
    const partiallyExtendedData = Object.entries(currentItem.data).reduce(
      (partiallyExtendedItemData, [key, value]) => {
        /** Функция для расширения свойства объекта из Items. Принимает ссылку на свойство,
         * которое проверяется, является ли оно типом ReferenceType и если да, то для него
         * будет искаться значение в стейте.
         * Ссылки на свойства для которых не удалось найти значения,
         * добавляются в nonExtendedReferences и uniqueNonExtendedReferences
         * для последующего поиска.
         */
        const expandData = (data: any) => {
          const isReference = instanceOfReference(data);
          /** Расширяются только значения типа reference */
          if (!isReference) {
            return data;
          }

          /** Пробуем найти значение для ReferenceType в state */
          const referenceItem = referencesSelectors.selectById(referencesState, data.id);

          // Если данные для данного ReferenceType отсутствует, добавляем его к массиву
          // ссылок, которые нужно попытаться расширить позже.
          // Так же добавляем этот ReferenceType в map ReferenceType, для которых нужно запросить данные.
          if (!referenceItem) {
            nonExtendedReferences.push(data);

            if (!uniqueNonExtendedReferences.has(data.id)) {
              uniqueNonExtendedReferences.set(data.id, data);
            }
            return data;
          }

          // Если у значения reference нет поля data, пропускаем эту ссылку.
          if (!referenceItem.data) {
            return data;
          }

          // displayName используется в проекте для отображения текстовой информации ReferenceType.
          // при этом позволяет сохранить оригинальные значения ReferenceType.
          const displayName = referenceItem.data.alternateName || referenceItem.data.name;

          // Расширяем ReferenceType, полем displayName и другими значениями из referenceItem.
          // аналогичная логика в modifyReferences()
          return {
            ...data,
            ...referenceItem.data,
            displayName,
          };
        };

        let extendedValue;
        if (Array.isArray(value)) {
          extendedValue = value.map(expandData);
        } else {
          extendedValue = expandData(value);
        }

        return {
          ...partiallyExtendedItemData,
          [key]: extendedValue,
        };
      },
      currentItem.data,
    );

    partiallyExtendedItems.push({
      ...currentItem,
      data: partiallyExtendedData,
    });
  }

  return {
    partiallyExtendedItems,
    nonExtendedReferences,
    uniqueNonExtendedReferences: Array.from(uniqueNonExtendedReferences.values()),
  };
};

export default getPartiallyExtendedItems;
